<template>
    <div v-if="!isLoadingMe && !isLoadingStandGuidance" class="appointment form-dimensioning">

        <transition name="fade">
            <div v-if="hasBookedAppointment" class="appointment__overlay">
                <span class="appointment__overlay-intro">{{ t('sie-haben-folgenden-termin-reserviert') }}</span>
                <span class="appointment__overlay-appointment">
                    <span>{{ getFullDate }}</span>
                    <span>{{ getDateByUserAppointment().time }}</span>
                    <button-element
                        type="secondary"
                        @click="cancelAppointment"
                        :disabled="isCancelling"
                        :button="{
                            'label': t('termin-stornieren')
                        }"
                    >
                        <span slot="icon" v-if="isCancelling">
                            <loader-element />
                        </span>
                    </button-element>
                </span>
                <div class="buttons">
                    <button-element
                        @click="handleClose"
                        :button="{
                            'label': t('schliessen'),
                        }"
                    ></button-element>
                </div>
            </div>
        </transition>

        <div :class="{
                'appointment__book-wrapper': true,
                'appointment__book-wrapper--disabled': bookingDisabled || hasBookedAppointment,
            }"
        >
            <div class="appointment__chose-date">
                <div
                    @click="() => handleDateClicked(index)"
                    v-for="(dateEntry, index) in standGuidance.dates"
                    :key="`appointment-date-${index}`"
                    :class="{
                        'appointment__calendar-item': true,
                        'appointment__calendar-item--active': activeDateIndex == index,
                    }"
                >
                    <span class="appointment__calendar-item-day">{{ dateEntry.date }}</span>
                    <span class="appointment__calendar-item-weekday">{{ getWeekday(dateEntry.day) }}</span>
                </div>
            </div>

            <div class="appointment__book-date">
                <template v-if="activeDateIndex !== null">
                    <div class="appointment__headline">
                        <span class="appointment__headline-day">{{ getActiveDate.date }}</span>
                        <span class="appointment__headline-weekday">{{ getWeekday(getActiveDate.day) }}</span>
                    </div>
                    <p class="appointment__dates-headline">Verfügbare Termine</p>
                    <div class="appointment__times">
                        <span
                            @click="() => handleTimeClicked(timeIndex)"
                            v-for="(timeData, timeIndex) in getActiveDate.times"
                            :key="`appointment-time-${timeIndex}`"
                            :class="{
                                'appointment__time': true,
                                'appointment__time--active': activeTimeIndex !== null && activeTimeIndex == timeIndex,
                                'appointment__time--reserved': appointmentIsReserved(timeData.timestamp),
                            }">
                                {{ timeData.time }} <span class="appointment__time-label">({{ availableAppointments(timeData.timestamp) }})</span>
                            </span>

                    </div>
                    <div v-if="!hasBookedAppointment" class="appointment__buttons">
                        <button-element
                            @click="bookAppointment"
                            :disabled="activeTimeIndex === null || isBooking"
                            :button="{
                                'label': t('termin-reservieren')
                            }"
                        >
                            <span slot="icon" v-if="isBooking">
                                <loader-element />
                            </span>
                        </button-element>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import store from "@/store";
import {
    UITEXTS_FETCH_NS,
    STANDGUIDANCE_FETCH_NS,
    STANDGUIDANCE_BOOKED_NS,
    STANDGUIDANCE_BOOK_NS,
    STANDGUIDANCE_CANCEL_NS,
} from "@/store/actions.type";

import ImageElement from '@/components/elements/ImageElement.vue';
import SelectElement from '@/components/elements/form/SelectElement.vue';
import InputElement from '@/components/elements/form/InputElement.vue';
import LoaderElement from '@/components/elements/LoaderElement.vue';

export default {
    name: 'StandGuidanceElement',
    components: {
        ImageElement,
        SelectElement,
        LoaderElement,
        InputElement,
    },
    props: {
        standGuidance: {
            type: Object,
            required: true
        }
    },
    computed: {
        ...mapGetters('uitexts', {
            t: 't',
        }),

        ...mapGetters('me', {
            isLoadingMe: 'isLoading',
            me: 'getMe',
        }),

        ...mapGetters('standGuidance', {
            isLoadingStandGuidance: 'isLoading',
            isBookingStandGuidance: 'isBooking',
            isCancellingStandGuidance: 'isCancelling',
            getAppointments: 'getAppointments',
            getUserAppointment: 'getUserAppointment',
        }),

        getActiveDate() {
            return this.activeDateIndex !== null ? this.standGuidance.dates[this.activeDateIndex] : null;
        },

        buttonsDisabled() {
            return this.bookingDisabled || this.isBooking;
        },

        hasBookedAppointment() {
            return this.getUserAppointment != null;
        },

        getFullDate() {
            const date = new Date(this.getUserAppointment * 1000);
            return date.toLocaleDateString();
        },
    },

    data: () => {
        return {
            activeDateIndex: null,
            activeTimeIndex: null,
            bookingDisabled: false,
        }
    },

    async created() {
        await store.dispatch(STANDGUIDANCE_FETCH_NS);
        await store.dispatch(STANDGUIDANCE_BOOKED_NS);
    },

    mounted() {

    },

    methods: {
        handleClose() {
            this.$emit('close');
        },

        handleDateClicked(index) {
            if (!this.buttonsDisabled) {
                this.activeTimeIndex = null;
                this.activeDateIndex = index;
            }
        },

        handleTimeClicked(index) {
            if (!this.buttonsDisabled) {
                this.activeTimeIndex = index;
            }
        },

        async cancelAppointment() {

            if (this.isCancelling) { return; }

            const cancellationResult = await store.dispatch(STANDGUIDANCE_CANCEL_NS, {
                action: 'guestmanagement/stand-guidance/cancel'
            });

            this.activeDateIndex = null;
            this.activeTimeIndex = null;
        },

        async bookAppointment() {
            if (!this.buttonsDisabled) {

                const appointment = this.getActiveDate.times[this.activeTimeIndex];

                const bookingResult = await store.dispatch(STANDGUIDANCE_BOOK_NS, {
                    appointment: appointment.timestamp,
                    action: 'guestmanagement/stand-guidance/book'
                });
            }
        },

        appointmentIsReserved(timestamp) {
            if (!this.getAppointments.hasOwnProperty(timestamp)) {
                return false;
            }

            return this.getAppointments[timestamp] >= this.standGuidance.maxParticipants;
        },

        availableAppointments(timestamp) {

            if (!this.getAppointments.hasOwnProperty(timestamp)) {
                return this.standGuidance.maxParticipants;
            }

            return Math.max(0, this.standGuidance.maxParticipants - this.getAppointments[timestamp]);
        },

        getWeekday(day) {
            return [
                'Montag',
                'Dienstag',
                'Mittwoch',
                'Donnerstag',
                'Freitag',
                'Samstag',
                'Sonntag',
            ][parseInt(day) - 1];
        },

        getDateByUserAppointment() {

            const result = {};
            const date = this.standGuidance.dates.forEach((date) => {

                date.times.forEach((time) => {
                    if (time.timestamp == this.getUserAppointment) {
                        result['date'] = date['date'];
                        result['time'] = time['time'];
                    }
                })
            });

            return result;
        },
    }
}
</script>
